import {Component, Input, OnInit} from '@angular/core';
import {Currency, ProcessSheets, Product, ProductTreeElement, ResourceKey, TId} from '../../../../../../models';
import {GenericReadOnlyResource, IGenericListOptions} from "../../../../../../resources";
import {MasterApiService} from "../../../../../../services/master-api.service";
import {firstValueFrom, Observable, take} from "rxjs";
import {GenericCrudEditComponent} from "../../../../../../libs/components/generic-crud/generic-crud-edit.component";
import {TabRole, TabsService} from "../../../../../../libs/components/tabs-host/tabs.service";
import {
  ProductsProcessSheetsEditComponent
} from "../../products-process-sheets-edit/products-process-sheets-edit.component";
import {ProductsEditComponent} from "../../../products-operative-products/products-edit/products-edit.component";
import {
  ProductPricesComponent
} from "../../../products-operative-products/components/product-prices/product-prices.component";
import {
  ProductStockComponent
} from "../../../products-operative-products/components/product-stock/product-stock.component";
import {
  ProductProcesSheetsComponent
} from "../../../products-operative-products/product-proces-sheets/product-proces-sheets.component";
import {
  ProductSimulationsComponent
} from "../../../products-operative-products/product-simulations/product-simulations.component";
import {
  ProductSubscribersComponent
} from "../../../products-operative-products/components/product-subscribers/product-subscribers.component";
import {AclService} from "../../../../../../libs/services/acl.service";
import {StorageService} from "../../../../../../services/storage.service";

export class ProductAncestorsDescendantsResource
  extends GenericReadOnlyResource<ProductTreeElement> {

  // treeOrder is not unique on its own (the same value repeats across depths),
  // so a composite key with depth is used to give every row a unique key and
  // avoid DevExtreme's E1040 duplicate-key error.
  override key = ['treeOrder', 'depth'] as unknown as ResourceKey<ProductTreeElement>

  constructor(
    protected override api: MasterApiService,
    private productId: TId,
    private type: 'ancestors' | 'descendants'
  ) {
    super(api, 'Products');
  }

  override list(
    options: IGenericListOptions
  ): Observable<{ total: number; data: ProductTreeElement[] }> {
    options.pagination = options.pagination ?? { page: 1, size: 20 };

    if (this.type === 'descendants') {
      return this.api.getListDescendants(this.productId, options);
    }

    return this.api.getListAncestors(this.productId, options);
  }
}

@Component({
  selector: 'app-product-ancestors-descendants',
  standalone: false,
  templateUrl: './product-ancestors-descendants.component.html',
  styleUrl: './product-ancestors-descendants.component.sass'
})
export class ProductAncestorsDescendantsComponent implements OnInit {

  @Input() type: 'ancestors' | 'descendants' = 'ancestors';
  @Input() productId?: TId;

  editProcessSheetComponent = ProductsProcessSheetsEditComponent;
  productEditComponent = ProductsEditComponent;
  resource?: ProductAncestorsDescendantsResource;
  currency?: Currency;

  constructor(private api: MasterApiService, private tabs: TabsService, private acl: AclService, private storage: StorageService ) {}

  ngOnInit(): void {
    if (!this.productId) {
      this.resource = undefined;
      return;
    }

    this.resource = new ProductAncestorsDescendantsResource(
      this.api,
      this.productId,
      this.type
    );
    this.currency = this.storage.companyCurrency();
  }

  openProduct(element: ProductTreeElement) {

    const cmpt = this.tabs.createTab<GenericCrudEditComponent<Product>>(
      this.productEditComponent!,
      TabRole.EDITION,
      { inputPrms: this.getInputPrms(element.productId) }
    );
  }

  async openProcessSheet(element: ProductTreeElement){

    const processSheet: ProcessSheets = await firstValueFrom(this.api.resources.product.processSheetsRevisions(element.processSheetId, element.processSheetVersionId).get(element.processSheetRevisionId)) as unknown as ProcessSheets;

    this.navigateToProcessSheetEditor(processSheet)
  }

  navigateToProcessSheetEditor(element: ProcessSheets) {

    const cmpt = this.tabs.createTab<GenericCrudEditComponent<ProcessSheets>>(
      this.editProcessSheetComponent!,
      TabRole.EDITION,
      { inputPrms: { entity: element } }
    );
  }

  getInputPrms(row: any): any {
    const innerEditors: any[] = []
    innerEditors.push({
      name: 'price',
      component: ProductPricesComponent,
      inputPrms: { id: row }
    })
    innerEditors.push({
      name: 'stock',
      component: ProductStockComponent,
      inputPrms: { id: row }
    })
    innerEditors.push({
      name:'processsheets',
      component: ProductProcesSheetsComponent,
      inputPrms: { productId: row }
    })
    innerEditors.push({
      name:'simulations',
      component: ProductSimulationsComponent,
      inputPrms: { id: row }
    })
    if (this.acl.isAdmin()) {
      innerEditors.push({
        name: 'subscribers',
        component: ProductSubscribersComponent,
        inputPrms: { id: row },
        isAdminConfig: true
      });
    }
    return {
      //productFamilies: this.productFamilies,
      id: row,
      innerEditors: innerEditors,
    };
  }


}
